import React, { FC } from 'react'
import { Box, Grid, makeStyles, Typography } from 'transfix-ui/core'
import { IAs2Certificate, IUpdateAs2CertificateInput } from 'types/graphqlTypes'
import { SuccessIcon } from 'transfix-icons'
import { Button, Card, InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui'
import { useModalState } from 'utils/hooks'
import { BoldSubject } from './BoldSubject'
import { useAs2Certificate } from './hooks/useAs2Certificate'
import { ActivateAs2CertificateConfirmationModal } from './modals/ActivateAs2CertificateConfirmationModal'
import { EditAs2CertificateModal } from './modals/EditAs2CertificateModal'
import { IAs2CertificateSchema } from './schema/As2CertificateSchema'

interface IAS2CertificatesProps {
  /**
   * @example '1'
   */
  as2ConnectionConfigId?: string
  as2Certificate?: IAs2Certificate
  onBackClick: () => void
  displayUserFeedback: (message: string, level: 'error' | 'info') => void
}

const useAS2CertificateDetailsStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: 'none',
    boxShadow: 'none',
  },
  button: {
    marginBottom: theme.spacing(2),
  },
  container: {
    marginBottom: theme.spacing(2),
  },
  text: {
    whiteSpace: 'pre-line',
  },
  gridItem: {
    display: 'flex',
    alignItems: 'center',
  },
}))

export const AS2CertificateDetails: FC<IAS2CertificatesProps> = ({
  as2Certificate,
  as2ConnectionConfigId,
  displayUserFeedback,
  onBackClick,
}) => {
  const { body, id = null, isActive, notAfter, notBefore, subject = null, x509ReadError = null } =
    as2Certificate || {}

  const { createToggleHandler, modalState } = useModalState({
    dispatchActivateModal: false,
    dispatchEditModal: false,
  })
  const openActivateHandler = createToggleHandler('dispatchActivateModal', true)
  const closeActivateHandler = createToggleHandler('dispatchActivateModal', false)

  const openEditHandler = createToggleHandler('dispatchEditModal', true)
  const closeEditHandler = createToggleHandler('dispatchEditModal', false)

  const classes = useAS2CertificateDetailsStyles()

  const { updateAs2Certificate } = useAs2Certificate({
    displayUserFeedback,
  })

  const submitHandler = (data: IAs2CertificateSchema) => {
    const input: IUpdateAs2CertificateInput = {
      id: data?.id || '',
      body: data?.body,
    }

    updateAs2Certificate(input)
  }

  const cells = [
    { label: 'Certificate Body', value: `${body}` },
    { label: 'Valid At', value: notBefore },
    { label: 'Expires At', value: notAfter! },
    { label: 'Hostname', value: <BoldSubject subject={subject} /> },
    {
      label: 'Active Status',
      value: isActive ? (
        <SuccessIcon verticalAlign='text-top' size='xlarge' color='green2' />
      ) : (
        <Button
          disabled={Boolean(x509ReadError)}
          label='Activate'
          color='primary'
          size='small'
          onClick={openActivateHandler}>
          Activate
        </Button>
      ),
    },
  ]

  if (!as2Certificate) {
    return null
  }

  return (
    <div>
      {modalState.dispatchActivateModal && id && (
        <ActivateAs2CertificateConfirmationModal
          open={modalState.dispatchActivateModal}
          onClose={closeActivateHandler}
          as2CertificateId={id}
          onDisplayUserFeedback={displayUserFeedback}
          onSuccess={onBackClick}
        />
      )}

      {modalState.dispatchEditModal && (
        <EditAs2CertificateModal
          open={modalState.dispatchEditModal}
          onClose={closeEditHandler}
          title='Update AS2 Certificate'
          onSubmit={submitHandler}
          defaultValues={{ id, body, x509ReadError }}
          onSuccess={onBackClick}
        />
      )}
      <Grid container direction='row' justifyContent='space-between' className={classes.container}>
        <Grid item lg='auto' sm='auto' xs='auto' md='auto'>
          <Button
            color='secondary'
            className={classes.button}
            size='small'
            data-test='back-to-as2-certificates-list'
            onClick={onBackClick}>
            Back
          </Button>
        </Grid>
        <Grid item lg='auto' sm='auto' xs='auto' md='auto'>
          <Button
            color='secondary'
            className={classes.button}
            size='small'
            data-test='edit-as2-certificate'
            onClick={openEditHandler}>
            Edit
          </Button>
        </Grid>
      </Grid>
      {x509ReadError && (
        <Box mb={2}>
          <InlineMessage
            dataTest='message-errors'
            type={INLINE_MESSAGE_VARIANTS.ALERT}
            message='This certificate cannot be activated because it is an invalid certificate. Please update the valid details and activate'
            className='mr2'
          />
        </Box>
      )}
      <Card className={classes.card}>
        {cells.map(cell => (
          <Grid
            container
            justifyContent='flex-start'
            spacing={1}
            key={`cell-${as2ConnectionConfigId}-${cell.label}`}
            className={classes.container}>
            <Grid item lg={2} sm={12} xs={12} md={2}>
              <Typography variant='h6'>{cell.label}</Typography>
            </Grid>
            <Grid item className={classes.gridItem} lg={10} sm={12} xs={12} md={10}>
              <span className={classes.text}>{cell.value}</span>
            </Grid>
          </Grid>
        ))}
      </Card>
    </div>
  )
}
